import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import { NotesIcon } from '../../components/notes/NotesIcon';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import { toast } from 'sonner';
import { 
  Building, Plus, Edit, Trash2, Search, RefreshCw, Gauge, Zap, Flame, Droplet, 
  Link, PlusCircle, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle,
  Eye, Key, CreditCard, ExternalLink, X, Wind, Sun, Snowflake, Loader2, ShieldCheck, XCircle,
  Bot, Play, CalendarClock, Info, ChevronRight
} from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import {
  TooltipProvider, Tooltip, TooltipTrigger, TooltipContent,
} from '../../components/ui/tooltip';
import { SupplierCombobox } from '../../components/collectors/SupplierCombobox';
import { AccountLinkDialog } from './AccountLinkDialog';
import { CollectorOnboardDialog } from '../../components/collectors/CollectorOnboardDialog';
import { CollectorBotsTab } from '../../components/collectors/CollectorBotsTab';

const VendorAccounts = () => {
  const navigate = useNavigate();
  const [currentAccount, setCurrentAccount] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [utilityAccounts, setUtilityAccounts] = useState([]);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [portalCredentials, setPortalCredentials] = useState([]);
  const [collectorSuppliers, setCollectorSuppliers] = useState([]);
  const [collectingId, setCollectingId] = useState(null);
  // Collector accounts (accounts mapped to a credential's Supplier, from botaccounts)
  const [collectorCounts, setCollectorCounts] = useState({});   // { supplier: count }
  const [expandedCredId, setExpandedCredId] = useState(null);
  const [collectorAccts, setCollectorAccts] = useState({});     // { credId: {loading, accounts, count, error} }
  const [loading, setLoading] = useState(false);
  const [loadingMoreAccounts, setLoadingMoreAccounts] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [commodityFilter, setCommodityFilter] = useState('all');
  const [caStatusFilter, setCaStatusFilter] = useState('all');
  const [unitStatusFilter, setUnitStatusFilter] = useState('all');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('vendors');
  
  // Utility accounts lazy loading
  const [accountsPage, setAccountsPage] = useState(1);
  const [hasMoreAccounts, setHasMoreAccounts] = useState(true);
  const [totalAccountsCount, setTotalAccountsCount] = useState(0);
  const accountsSentinelRef = useRef(null);
  const searchTimerRef = useRef(null);

  // Unit accounts state
  const [unitAccounts, setUnitAccounts] = useState([]);
  const [unitAccountsPage, setUnitAccountsPage] = useState(1);
  const [hasMoreUnitAccounts, setHasMoreUnitAccounts] = useState(true);
  const [totalUnitAccountsCount, setTotalUnitAccountsCount] = useState(0);
  const [loadingUnitAccounts, setLoadingUnitAccounts] = useState(false);
  const unitAccountsSentinelRef = useRef(null);
  
  // Modal states
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [showVendorDetailModal, setShowVendorDetailModal] = useState(false);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddCredentialModal, setShowAddCredentialModal] = useState(false);
  const [showOnboardDialog, setShowOnboardDialog] = useState(false);
  const [showEditCredentialModal, setShowEditCredentialModal] = useState(false);
  const [showDeleteCredentialConfirm, setShowDeleteCredentialConfirm] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [vendorDetails, setVendorDetails] = useState(null);
  const [saving, setSaving] = useState(false);
  const [vendorDetailTab, setVendorDetailTab] = useState('details');
  const [showEditAccountModal, setShowEditAccountModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  // Meter/Identifier linking (Feature 3)
  const [identifiers, setIdentifiers] = useState([]);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkDialogAcct, setLinkDialogAcct] = useState(null);
  
  // Location/Space/Meter data for linking
  const [locations, setLocations] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [meters, setMeters] = useState([]);
  
  // CSV Upload states
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // New vendor form
  const [vendorForm, setVendorForm] = useState({
    vendor_name: '',
    support_phone: '',
    email: '',
    account_number: '',
    account_commodity: 'electricity',
    status: 'active',
    frequency: '',
    bill_source: '',
    service_address: '',
    service_city: '',
    service_state: '',
    service_country: 'USA',
    service_zip: '',
    account_start: '',
    account_end: '',
    account_name: '',
    missing_bills_expected_start_date: '',
    has_parent_account: false,
    parent_account_number: '',
  });

  // Portal Credential form
  const [credentialForm, setCredentialForm] = useState({
    vendor_id: '',
    account_name: '',
    portal_url: '',
    username: '',
    password: '',
    account_number: '',
    bp_credential_id: '',
    collector_supplier: '',
    collector_supplier_name: '',
  });

  // Org vendor settings form
  const [orgSettingsForm, setOrgSettingsForm] = useState({
    pms_alias_name: '',
    pms_vendor_code: '',
    preferred_payment_method: '',
    ach_routing_number: '',
    ach_account_number: '',
  });

  // Account form (for add and edit)
  const [accountForm, setAccountForm] = useState({
    vendor_id: '',
    account_number: '',
    account_name: '',
    commodity: 'electricity',
    service_address: '',
    service_city: '',
    service_state: '',
    service_zip: '',
    meter_number: '',
    linked_location_id: '',
    linked_space_id: '',
    linked_meter_id: '',
    bp_entity_id: '',
    status: 'active',
    account_start: '',
    account_end: '',
    missing_bills_expected_start_date: '',
    frequency: '',
    bill_source: '',
    has_parent_account: false,
    parent_account_number: '',
  });

  const commodities = [
    { value: 'electricity', label: 'Electricity', icon: Zap, color: 'text-yellow-600' },
    { value: 'gas', label: 'Gas', icon: Flame, color: 'text-orange-600' },
    { value: 'water', label: 'Water', icon: Droplet, color: 'text-blue-600' },
    { value: 'sewer', label: 'Sewer', icon: Droplet, color: 'text-purple-600' },
    { value: 'stormwater', label: 'Stormwater', icon: Droplet, color: 'text-teal-600' },
    { value: 'trash', label: 'Trash', icon: Wind, color: 'text-gray-700' },
    { value: 'internet', label: 'Internet', icon: Gauge, color: 'text-indigo-600' },
    { value: 'cable', label: 'Cable', icon: Gauge, color: 'text-red-600' },
    { value: 'phone', label: 'Phone', icon: Gauge, color: 'text-sky-600' },
    { value: 'ev_charger', label: 'EV Charger', icon: Zap, color: 'text-green-600' },
    { value: 'solar', label: 'Solar', icon: Sun, color: 'text-amber-500' },
    { value: 'steam', label: 'Steam', icon: Wind, color: 'text-gray-600' },
    { value: 'chilled_water', label: 'Chilled Water', icon: Snowflake, color: 'text-cyan-600' },
  ];

  // Commodity options for the Add-Vendor utility-account form (per product spec)
  const accountCommodities = [
    { value: 'electricity', label: 'Electricity' },
    { value: 'water', label: 'Water' },
    { value: 'gas', label: 'Gas' },
    { value: 'waste', label: 'Waste' },
    { value: 'mobile_charges', label: 'Mobile Charges' },
    { value: 'telecom', label: 'Telecom' },
    { value: 'dual', label: 'Dual' },
  ];

  useEffect(() => {
    const initializePage = async () => {
      try {
        const { data: accounts } = await api.get('/auth/accounts');
        const storedAccountStr = localStorage.getItem('current_account');
        const storedAccount = storedAccountStr ? JSON.parse(storedAccountStr) : null;
        
        let account = null;
        if (storedAccount) {
          account = accounts.find(a => a.id === storedAccount.id);
        }
        if (!account && accounts.length > 0) {
          account = accounts[0];
        }
        
        if (!account) {
          navigate('/dashboard');
          return;
        }

        if (!account.enabled_apps?.ubm) {
          navigate('/dashboard');
          return;
        }

        setCurrentAccount(account);
        localStorage.setItem('current_account', JSON.stringify(account));
      } catch (error) {
        console.error('Failed to initialize:', error);
      } finally {
        setInitializing(false);
      }
    };

    initializePage();
  }, [navigate]);

  useEffect(() => {
    if (currentAccount && !initializing) {
      fetchData();
    }
  }, [currentAccount, initializing]);

  // Load Portal Collector supplier list (for the credential Supplier picker).
  useEffect(() => {
    if (!currentAccount) return;
    api.get('/portal-collectors/suppliers', { params: { account_id: currentAccount.id } })
      .then(({ data }) => setCollectorSuppliers(data || []))
      .catch(() => {});
  }, [currentAccount]);

  // Per-credential account counts (login-scoped, from MySQL botaccounts).
  useEffect(() => {
    if (!currentAccount) return;
    api.get(`/ubm/accounts/${currentAccount.id}/portal-credentials/collector-account-counts`)
      .then(({ data }) => setCollectorCounts(data?.by_credential || {}))
      .catch(() => setCollectorCounts({}));
  }, [currentAccount, portalCredentials.length]);

  // Org vendor settings map (vendor_id → settings)
  const [orgVendorSettingsMap, setOrgVendorSettingsMap] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vendorsRes, accountsRes, availableRes, locationsRes, spacesRes, metersRes, credentialsRes, unitAccountsRes, orgVendorSettingsRes, identifiersRes] = await Promise.all([
        api.get(`/ubm/accounts/${currentAccount.id}/vendor-accounts`),
        api.get(`/ubm/accounts/${currentAccount.id}/utility-accounts?page=1&page_size=100`),
        api.get('/ubm/vendors'),
        api.get(`/ubm/accounts/${currentAccount.id}/locations`),
        api.get(`/ubm/accounts/${currentAccount.id}/spaces?page_size=200`),
        api.get(`/ubm/accounts/${currentAccount.id}/meters?page_size=200`),
        api.get(`/ubm/accounts/${currentAccount.id}/portal-credentials`),
        api.get(`/ubm/accounts/${currentAccount.id}/unit-accounts?page=1&page_size=100`),
        api.get(`/ubm/accounts/${currentAccount.id}/org-vendor-settings`).catch(() => ({ data: {} })),
        api.get('/ubm-migration/entities', { params: { account_id: currentAccount.id, page_size: 2000 } }).catch(() => ({ data: { items: [] } })),
      ]);
      
      setLocations(locationsRes.data || []);
      setIdentifiers(identifiersRes.data?.items || []);
      const spacesData = spacesRes.data?.items || spacesRes.data || [];
      setSpaces(spacesData);
      const metersData = metersRes.data?.items || metersRes.data || [];
      setMeters(metersData);
      setPortalCredentials(credentialsRes.data || []);
      
      // Store org vendor settings map
      const ovSettings = orgVendorSettingsRes.data || {};
      setOrgVendorSettingsMap(ovSettings);
      
      // Enrich vendor accounts with vendor info
      const vendorMap = {};
      (availableRes.data || []).forEach(v => { vendorMap[v.id] = v; });
      
      const enrichedVendors = (vendorsRes.data || []).map(va => ({
        ...va,
        vendor_name: vendorMap[va.vendor_id]?.vendor_name || va.vendor_name || 'Unknown',
        vendor_code: vendorMap[va.vendor_id]?.vendor_code,
        bp_supplier_id: vendorMap[va.vendor_id]?.bp_supplier_id,
      }));
      
      setVendors(enrichedVendors);
      // Handle paginated utility accounts
      const acctData = accountsRes.data;
      setUtilityAccounts(acctData.items || acctData || []);
      setTotalAccountsCount(acctData.total || (acctData.items || acctData || []).length);
      setAccountsPage(1);
      setHasMoreAccounts((acctData.total_pages || 0) > 1);
      setAvailableVendors(availableRes.data || []);

      // Handle unit accounts
      const uaData = unitAccountsRes.data;
      setUnitAccounts(uaData.items || uaData || []);
      setTotalUnitAccountsCount(uaData.total || (uaData.items || uaData || []).length);
      setUnitAccountsPage(1);
      setHasMoreUnitAccounts((uaData.total_pages || 0) > 1);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreAccounts = useCallback(async () => {
    if (!currentAccount || loadingMoreAccounts || !hasMoreAccounts) return;
    setLoadingMoreAccounts(true);
    const nextPage = accountsPage + 1;
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/utility-accounts?page=${nextPage}&page_size=100`);
      const newItems = data.items || [];
      setUtilityAccounts(prev => [...prev, ...newItems]);
      setAccountsPage(nextPage);
      setHasMoreAccounts(nextPage < (data.total_pages || 0));
    } catch (error) {
      console.error('Failed to load more accounts:', error);
      setHasMoreAccounts(false);
    } finally {
      setLoadingMoreAccounts(false);
    }
  }, [currentAccount, accountsPage, loadingMoreAccounts, hasMoreAccounts]);

  const loadMoreUnitAccounts = useCallback(async () => {
    if (!currentAccount || loadingUnitAccounts || !hasMoreUnitAccounts) return;
    setLoadingUnitAccounts(true);
    const nextPage = unitAccountsPage + 1;
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/unit-accounts?page=${nextPage}&page_size=100`);
      const newItems = data.items || [];
      setUnitAccounts(prev => [...prev, ...newItems]);
      setUnitAccountsPage(nextPage);
      setHasMoreUnitAccounts(nextPage < (data.total_pages || 0));
    } catch (error) {
      console.error('Failed to load more unit accounts:', error);
      setHasMoreUnitAccounts(false);
    } finally {
      setLoadingUnitAccounts(false);
    }
  }, [currentAccount, unitAccountsPage, loadingUnitAccounts, hasMoreUnitAccounts]);

  // Scroll-based lazy loading for utility accounts (works inside tabs)
  useEffect(() => {
    if (loading) return;
    const isAccounts = activeTab === 'accounts' && hasMoreAccounts && !loadingMoreAccounts;
    const isUnits = activeTab === 'unit-accounts' && hasMoreUnitAccounts && !loadingUnitAccounts;
    if (!isAccounts && !isUnits) return;
    
    const handleScroll = () => {
      const scrollBottom = window.innerHeight + window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollBottom >= docHeight - 300) {
        if (activeTab === 'accounts' && !loadingMoreAccounts && hasMoreAccounts) loadMoreAccounts();
        if (activeTab === 'unit-accounts' && !loadingUnitAccounts && hasMoreUnitAccounts) loadMoreUnitAccounts();
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMoreAccounts, hasMoreUnitAccounts, loading, loadingMoreAccounts, loadingUnitAccounts, activeTab, loadMoreAccounts, loadMoreUnitAccounts]);

  const handleAddVendor = async (e) => {
    e.preventDefault();
    if (!vendorForm.vendor_name.trim()) {
      toast.error('Supplier name is required');
      return;
    }
    
    setSaving(true);
    try {
      const { data: vres } = await api.post('/ubm/vendors', {
        vendor_name: vendorForm.vendor_name,
        support_phone: vendorForm.support_phone,
        email: vendorForm.email,
        commodities_served: vendorForm.account_commodity ? [vendorForm.account_commodity] : [],
      });
      if (vendorForm.account_number.trim() && currentAccount?.id) {
        await api.post(`/ubm/accounts/${currentAccount.id}/utility-accounts`, {
          vendor_id: vres.id,
          account_number: vendorForm.account_number,
          account_name: vendorForm.account_name,
          commodity: vendorForm.account_commodity,
          status: vendorForm.status,
          frequency: vendorForm.frequency,
          bill_source: vendorForm.bill_source,
          service_address: vendorForm.service_address,
          service_city: vendorForm.service_city,
          service_state: vendorForm.service_state,
          service_country: vendorForm.service_country,
          service_zip: vendorForm.service_zip,
          account_start: vendorForm.account_start,
          account_end: vendorForm.account_end,
          missing_bills_expected_start_date: vendorForm.missing_bills_expected_start_date || null,
          has_parent_account: vendorForm.has_parent_account,
          parent_account_number: vendorForm.parent_account_number,
        });
        try {
          await api.patch('/intake/missing-bills/account-settings',
            { account_kind: 'vendor', account_number: vendorForm.account_number,
              start_date: vendorForm.missing_bills_expected_start_date || null },
            { params: { account_id: currentAccount.id } });
        } catch { /* non-blocking */ }
        toast.success('Vendor and utility account created');
      } else {
        toast.success('Vendor added successfully');
      }
      setShowAddVendorModal(false);
      resetVendorForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!accountForm.vendor_id || !accountForm.account_number.trim()) {
      toast.error('Vendor and account number are required');
      return;
    }
    
    setSaving(true);
    try {
      const { data } = await api.post(`/ubm/accounts/${currentAccount.id}/utility-accounts`, accountForm);
      await syncExpectedStart();
      toast.success(`Account created. Auto-linked: Property=${data.auto_linked?.location}, Space=${data.auto_linked?.space}, Meter=${data.auto_linked?.meter}`);
      setShowAddAccountModal(false);
      resetAccountForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add account');
    } finally {
      setSaving(false);
    }
  };

  const openLinkDialog = (account) => {
    setLinkDialogAcct(account);
    setShowLinkDialog(true);
  };

  const openEditAccount = (account) => {
    setEditingAccount(account);
    const toDateStr = (d) => d ? new Date(d).toISOString().split('T')[0] : '';
    setAccountForm({
      vendor_id: account.vendor_id || '',
      account_number: account.account_number || '',
      account_name: account.account_name || '',
      commodity: account.commodity || 'electricity',
      service_address: account.service_address || '',
      service_city: account.service_city || '',
      service_state: account.service_state || '',
      service_zip: account.service_zip || '',
      meter_number: account.meter_number || '',
      linked_location_id: account.linked_location_id || '',
      linked_space_id: account.linked_space_id || '',
      linked_meter_id: account.linked_meter_id || '',
      bp_entity_id: account.bp_entity_id || '',
      account_start: toDateStr(account.account_start),
      account_end: toDateStr(account.account_end),
      missing_bills_expected_start_date: toDateStr(account.missing_bills_expected_start_date),
      frequency: account.frequency || '',
      bill_source: account.bill_source || '',
      has_parent_account: account.has_parent_account || false,
      parent_account_number: account.parent_account_number || '',
    });
    setShowEditAccountModal(true);
  };

  // Mirror the Missing-Bills expected-start onto the flagger's source collection.
  const syncExpectedStart = async () => {
    if (!accountForm.account_number) return;
    try {
      await api.patch('/intake/missing-bills/account-settings',
        { account_kind: 'vendor', account_number: accountForm.account_number,
          start_date: accountForm.missing_bills_expected_start_date || null },
        { params: { account_id: currentAccount.id } });
    } catch { /* non-blocking */ }
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    if (!editingAccount) return;
    
    setSaving(true);
    try {
      await api.put(`/ubm/accounts/${currentAccount.id}/utility-accounts/${editingAccount.id}`, accountForm);
      await syncExpectedStart();
      toast.success('Account updated successfully');
      setShowEditAccountModal(false);
      setEditingAccount(null);
      resetAccountForm();
      fetchData();
      // Refresh vendor details if modal is open
      if (vendorDetails) {
        const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/vendors/${vendorDetails.vendor.id}/details`);
        setVendorDetails(data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  // Portal Credentials handlers
  const handleAddCredential = async (e) => {
    e.preventDefault();
    if (!credentialForm.collector_supplier) {
      toast.error('Supplier (Collector) is required');
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/ubm/accounts/${currentAccount.id}/portal-credentials`, credentialForm);
      toast.success('Credential added successfully');
      setShowAddCredentialModal(false);
      resetCredentialForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add credential');
    } finally {
      setSaving(false);
    }
  };

  // ── Collector accounts (mapped-to-Supplier) accordion ──────────────────
  const fetchCollectorAccounts = async (cred) => {
    setCollectorAccts(prev => ({ ...prev, [cred.id]: { ...(prev[cred.id] || {}), loading: true } }));
    try {
      const { data } = await api.get(
        `/ubm/accounts/${currentAccount.id}/portal-credentials/${cred.id}/collector-accounts`);
      setCollectorAccts(prev => ({ ...prev, [cred.id]: {
        loading: false, accounts: data.accounts || [], count: data.count || 0 } }));
    } catch (error) {
      setCollectorAccts(prev => ({ ...prev, [cred.id]: {
        loading: false, accounts: [], count: 0, error: true } }));
    }
  };

  const toggleExpandCredential = (cred) => {
    if (expandedCredId === cred.id) { setExpandedCredId(null); return; }
    setExpandedCredId(cred.id);
    if (!collectorAccts[cred.id]) fetchCollectorAccounts(cred);
  };

  const openEditCredential = (credential) => {
    setSelectedCredential(credential);
    setCredentialForm({
      vendor_id: credential.vendor_id || '',
      account_name: credential.account_name || '',
      portal_url: credential.portal_url || '',
      username: credential.username || '',
      password: '', // Don't pre-fill password for security
      account_number: credential.account_number || '',
      bp_credential_id: credential.bp_credential_id || '',
      collector_supplier: credential.collector_supplier || '',
      collector_supplier_name: credential.collector_supplier_name || '',
    });
    setShowEditCredentialModal(true);
  };

  const handleUpdateCredential = async (e) => {
    e.preventDefault();
    if (!selectedCredential) return;
    
    setSaving(true);
    try {
      // Only send password if it was changed
      const updateData = { ...credentialForm };
      if (!updateData.password) delete updateData.password;
      
      await api.put(`/ubm/accounts/${currentAccount.id}/portal-credentials/${selectedCredential.id}`, updateData);
      toast.success('Credential updated successfully');
      setShowEditCredentialModal(false);
      setSelectedCredential(null);
      resetCredentialForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update credential');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCredential = async () => {
    if (!selectedCredential) return;
    
    setSaving(true);
    try {
      await api.delete(`/ubm/accounts/${currentAccount.id}/portal-credentials/${selectedCredential.id}`);
      toast.success('Credential deleted');
      setShowDeleteCredentialConfirm(false);
      setSelectedCredential(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete credential');
    } finally {
      setSaving(false);
    }
  };

  const handleRunCollect = async (cred, force = false) => {
    setCollectingId(cred.id);
    try {
      const { data: run } = await api.post(
        `/portal-collectors/credentials/${cred.id}/run`, { force },
        { params: { account_id: currentAccount.id } },
      );
      toast.info(run.message || 'Bill collection started…');
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          // Advance the run on-demand (poll bot status → detect failure / ingest
          // → notify) so it resolves without waiting on the background worker.
          const { data: r } = await api.post(
            `/portal-collectors/runs/${run.id}/refresh`, {},
            { params: { account_id: currentAccount.id } },
          );
          if (r.status === 'in_progress' && attempts < 18) { setTimeout(poll, 8000); return; }
          if (r.status === 'completed') {
            const nn = (r.new_docs ?? r.docs_ingested ?? 0);
            const dup = (r.duplicates ?? 0);
            if (nn > 0) toast.success(`Imported ${nn} new bill(s)${dup ? ` · ${dup} already imported` : ''} → Intake Hub`);
            else if (dup > 0) toast.info(`No new bills — ${dup} already imported. Use "Re-import" to pull again.`);
            else toast.info('Reached the portal, but no bills were returned for this account.');
          }
          else if (r.status === 'failed') toast.error(`Collection failed: ${r.error || 'unknown error'}`);
          else if (r.status === 'timeout') toast.warning('Still collecting in the background — check back shortly');
          else toast.info('Collection is still running in the background');
          setCollectingId(null);
          fetchData();
        } catch { setCollectingId(null); }
      };
      setTimeout(poll, 8000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start collection');
      setCollectingId(null);
    }
  };

  const handleToggleSchedule = async (cred, enabled) => {
    // Optimistic UI: reflect the toggle immediately.
    setPortalCredentials((prev) => prev.map((c) => (c.id === cred.id ? { ...c, schedule_enabled: enabled } : c)));
    try {
      await api.put(
        `/portal-collectors/credentials/${cred.id}/schedule`, { enabled },
        { params: { account_id: currentAccount.id } },
      );
      toast.success(enabled ? 'Auto-collect turned on' : 'Auto-collect turned off');
      fetchData();
    } catch (e) {
      // Revert on failure.
      setPortalCredentials((prev) => prev.map((c) => (c.id === cred.id ? { ...c, schedule_enabled: !enabled } : c)));
      toast.error(e.response?.data?.detail || 'Failed to update schedule');
    }
  };

  const fmtColDate = (v) => (v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '\u2014');

  const connMeta = (s) => ({
    connected: { dot: 'bg-emerald-500', label: 'Connected', tip: 'Credentials verified \u2014 logged into the portal successfully.' },
    invalid: { dot: 'bg-rose-500', label: 'Invalid', tip: 'Login failed \u2014 check the username/password.' },
    no_bills: { dot: 'bg-amber-500', label: 'No bills', tip: 'Reached the portal, but no bills were available to collect.' },
  }[s] || { dot: 'bg-slate-300', label: 'Pending', tip: 'Not verified yet \u2014 trigger a collection.' });

  const resetCredentialForm = () => {
    setCredentialForm({
      vendor_id: '',
      account_name: '',
      portal_url: '',
      username: '',
      password: '',
      account_number: '',
      bp_credential_id: '',
      collector_supplier: '',
      collector_supplier_name: '',
    });
  };

  // Get filtered spaces based on selected location
  const getFilteredSpaces = () => {
    if (!accountForm.linked_location_id) return spaces;
    return spaces.filter(s => s.location_id === accountForm.linked_location_id);
  };

  // Get filtered meters based on selected location/space
  const getFilteredMeters = () => {
    if (accountForm.linked_space_id) {
      return meters.filter(m => m.space_id === accountForm.linked_space_id);
    }
    if (accountForm.linked_location_id) {
      return meters.filter(m => m.location_id === accountForm.linked_location_id);
    }
    return meters;
  };

  const openVendorDetails = async (vendorId) => {
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/vendors/${vendorId}/details`);
      setVendorDetails(data);
      const hasSavedBanking = !!(data.org_settings?.ach_routing_number || data.org_settings?.ach_account_number);
      setOrgSettingsForm({
        pms_alias_name: data.org_settings?.pms_alias_name || '',
        pms_vendor_code: data.org_settings?.pms_vendor_code || '',
        preferred_payment_method: data.org_settings?.preferred_payment_method || '',
        // If banking is already saved we mask the values until OTP verify;
        // the mask hints come from /vendor-ach/banking/{vid}/mask below.
        ach_routing_number: hasSavedBanking ? '' : (data.org_settings?.ach_routing_number || ''),
        ach_account_number: hasSavedBanking ? '' : (data.org_settings?.ach_account_number || ''),
        portal_url: data.org_settings?.portal_url || '',
        portal_username: data.org_settings?.portal_username || '',
        portal_password: data.org_settings?.portal_password || '',
        portal_account_number: data.org_settings?.portal_account_number || '',
      });
      setAchLocked(hasSavedBanking);
      setAchVerifyResult(null);
      // Fetch mask (last-4 hints) — runs without OTP, safe to call.
      refreshAchMask(vendorId);
      setShowVendorDetailModal(true);
      setVendorDetailTab('details');
    } catch (error) {
      toast.error('Failed to load vendor details');
    }
  };

  const handleSaveOrgSettings = async () => {
    if (!vendorDetails?.vendor?.id) return;

    // Block save if user picked ACH but verification has not been run / failed.
    if (orgSettingsForm.preferred_payment_method === 'ach') {
      if (!achVerifyResult || !achVerifyResult.success) {
        toast.error('Please click "Verify" and pass the ACH banking check before saving.');
        return;
      }
    }

    setSaving(true);
    try {
      // If banking values are present (either fresh form or modified
      // after OTP unlock) route them through the ENCRYPTED endpoint
      // (`PUT /api/vendor-ach/banking/{vid}`) instead of letting them
      // hit the legacy `org-settings` plaintext write path.
      const sendingAch = orgSettingsForm.preferred_payment_method === 'ach'
        && !!orgSettingsForm.ach_routing_number
        && !!orgSettingsForm.ach_account_number;
      if (sendingAch) {
        const params = currentAccount?.id ? { account_id: currentAccount.id } : {};
        await api.put(`/vendor-ach/banking/${vendorDetails.vendor.id}`, {
          routing_number: orgSettingsForm.ach_routing_number,
          account_number: orgSettingsForm.ach_account_number,
          account_type: 'checking',
        }, { params });
      }
      // Strip secrets from the legacy payload — the encrypted endpoint
      // already persisted them; the legacy doc holds non-ACH fields only.
      const legacyPayload = { ...orgSettingsForm };
      if (sendingAch) {
        legacyPayload.ach_routing_number = '';
        legacyPayload.ach_account_number = '';
      }
      await api.put(`/ubm/accounts/${currentAccount.id}/vendors/${vendorDetails.vendor.id}/org-settings`, legacyPayload);
      toast.success('Vendor settings saved · banking encrypted & locked.');
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/vendors/${vendorDetails.vendor.id}/details`);
      setVendorDetails(data);
      setOrgSettingsForm((f) => ({ ...f, ach_routing_number: '', ach_account_number: '' }));
      setAchLocked(true);
      setAchVerifyResult(null);
      refreshAchMask(vendorDetails.vendor.id);
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Failed to save settings';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Verify ACH routing + account numbers BEFORE saving. Format + ABA check
  // are run server-side; result is audit-logged. Required to enable Save
  // when payment method is ACH.
  const [achVerifying, setAchVerifying] = useState(false);
  const [achVerifyResult, setAchVerifyResult] = useState(null);

  const handleVerifyAch = async () => {
    if (!vendorDetails?.vendor?.id) return;
    setAchVerifying(true);
    setAchVerifyResult(null);
    try {
      const params = currentAccount?.id ? { account_id: currentAccount.id } : {};
      const { data } = await api.post('/vendor-ach/verify-banking', {
        routing_number: orgSettingsForm.ach_routing_number || '',
        account_number: orgSettingsForm.ach_account_number || '',
        supplier_id: vendorDetails.vendor.id,
      }, { params });
      setAchVerifyResult(data);
      if (data.success) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Verification failed';
      setAchVerifyResult({ success: false, message: msg, steps: [] });
      toast.error(msg);
    } finally {
      setAchVerifying(false);
    }
  };

  // Invalidate any prior Verify result when routing/account/method changes.
  // Cleared inline in the input onChange handlers below so we don't trip the
  // react-hooks/set-state-in-effect rule.

  // ── Vendor banking LOCK / OTP-reveal state ──────────────────────────────
  // Saved banking is hidden by default — user must enter an emailed OTP to
  // view or modify. Mirrors the SFTP credential lock pattern.
  const [achLocked, setAchLocked] = useState(true);            // true until OTP verified this session
  const [achMask, setAchMask] = useState(null);                // { routing_last4, account_last4, has_* }
  const [achRevealOpen, setAchRevealOpen] = useState(false);
  const [achRevealStep, setAchRevealStep] = useState('idle');  // idle | otp_sent
  const [achRevealRequestId, setAchRevealRequestId] = useState(null);
  const [achRevealCode, setAchRevealCode] = useState('');
  const [achRevealSending, setAchRevealSending] = useState(false);

  // Refresh masked banking metadata when the vendor details load.
  const refreshAchMask = async (vendorId) => {
    if (!vendorId || !currentAccount?.id) return;
    try {
      const { data } = await api.get(`/vendor-ach/banking/${vendorId}/mask`, {
        params: { account_id: currentAccount.id },
      });
      setAchMask(data);
      // If nothing saved → no lock to enforce, user is creating fresh.
      const hasAnything = data?.has_routing || data?.has_account;
      setAchLocked(hasAnything);
      if (hasAnything) {
        // Hide the existing values from the form until verified.
        setOrgSettingsForm((f) => ({ ...f, ach_routing_number: '', ach_account_number: '' }));
      }
    } catch {
      // Best-effort; if mask endpoint fails leave lock state untouched.
    }
  };

  const startAchReveal = async () => {
    if (!vendorDetails?.vendor?.id) return;
    setAchRevealSending(true);
    try {
      const params = currentAccount?.id ? { account_id: currentAccount.id } : {};
      const { data } = await api.post(
        `/vendor-ach/banking/${vendorDetails.vendor.id}/request-reveal`, {}, { params },
      );
      setAchRevealRequestId(data.request_id);
      setAchRevealStep('otp_sent');
      if (data.email_sent) {
        // Real email delivered — suppress the dev-only `test_otp` toast.
        toast.success(`Verification code emailed to ${data.email}`);
      } else if (data.test_otp) {
        toast.error(
          `Email delivery failed (${data.email_error || 'check provider config'}). ` +
          `Fallback code: ${data.test_otp}`,
          { duration: 12000 }
        );
      } else {
        toast.error('Could not send verification code — please contact support.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send verification code');
    } finally {
      setAchRevealSending(false);
    }
  };

  const submitAchReveal = async () => {
    if (!achRevealCode || !achRevealRequestId || !vendorDetails?.vendor?.id) return;
    setAchRevealSending(true);
    try {
      const params = currentAccount?.id ? { account_id: currentAccount.id } : {};
      const { data } = await api.post(
        `/vendor-ach/banking/${vendorDetails.vendor.id}/reveal`,
        { request_id: achRevealRequestId, code: achRevealCode },
        { params },
      );
      // Unlock the form + auto-populate plaintext. Mark as already-verified
      // so Save isn't blocked unless the user types a new value (the
      // onChange handlers clear achVerifyResult, requiring re-verify).
      setOrgSettingsForm((f) => ({
        ...f,
        ach_routing_number: data.ach_routing_number || '',
        ach_account_number: data.ach_account_number || '',
      }));
      setAchVerifyResult({
        success: true,
        message: 'Previously saved banking (re-verify required if you change values)',
        steps: [],
      });
      setAchLocked(false);
      setAchRevealOpen(false);
      setAchRevealStep('idle');
      setAchRevealRequestId(null);
      setAchRevealCode('');
      toast.success('Verified — banking unlocked for this session');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    } finally {
      setAchRevealSending(false);
    }
  };

  const closeAchReveal = () => {
    setAchRevealOpen(false);
    setAchRevealStep('idle');
    setAchRevealRequestId(null);
    setAchRevealCode('');
  };

  const handleDeleteAccount = async () => {
    if (!selectedAccount) return;
    
    setSaving(true);
    try {
      await api.delete(`/ubm/accounts/${currentAccount.id}/utility-accounts/${selectedAccount.id}`);
      toast.success('Account deleted');
      setShowDeleteConfirm(false);
      setSelectedAccount(null);
      fetchData();
      // Refresh vendor details if modal is open
      if (vendorDetails) {
        const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/vendors/${vendorDetails.vendor.id}/details`);
        setVendorDetails(data);
      }
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setSaving(false);
    }
  };

  // CSV Upload handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(`/ubm/accounts/${currentAccount.id}/utility-accounts/csv-template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'utility_accounts_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setUploading(true);
    setUploadResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const { data } = await api.post(`/ubm/accounts/${currentAccount.id}/utility-accounts/csv-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResults(data);
      
      if (data.created > 0 || data.updated > 0) {
        toast.success(`Created ${data.created}, Updated ${data.updated} accounts. Auto-linked: ${data.auto_linked}. Vendors created: ${data.vendors_created}`);
        fetchData();
      }
      
      if (data.errors?.length > 0) {
        toast.warning(`${data.errors.length} rows had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
      setUploadResults({ errors: [{ error: error.response?.data?.detail || 'Upload failed' }] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const resetVendorForm = () => {
    setVendorForm({
      vendor_name: '',
      support_phone: '',
      email: '',
      account_number: '',
      account_commodity: 'electricity',
      status: 'active',
      frequency: '',
      bill_source: '',
      service_address: '',
      service_city: '',
      service_state: '',
      service_country: 'USA',
      service_zip: '',
      account_start: '',
      account_end: '',
      account_name: '',
      missing_bills_expected_start_date: '',
      has_parent_account: false,
      parent_account_number: '',
    });
  };

  const handleExportVendorAccounts = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/vendor-accounts/export`, { responseType: 'text' });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vendor_accounts_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleExportUtilityAccounts = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/utility-accounts/export`, { responseType: 'text' });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `utility_accounts_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const resetAccountForm = () => {
    setAccountForm({
      vendor_id: '',
      account_number: '',
      account_name: '',
      commodity: 'electricity',
      service_address: '',
      service_city: '',
      service_state: '',
      service_zip: '',
      meter_number: '',
      linked_location_id: '',
      linked_space_id: '',
      linked_meter_id: '',
      bp_entity_id: '',
      status: 'active',
      account_start: '',
      account_end: '',
      missing_bills_expected_start_date: '',
      frequency: '',
      bill_source: '',
      has_parent_account: false,
      parent_account_number: '',
    });
  };

  const toggleCommodity = (value) => {
    setVendorForm(prev => ({
      ...prev,
      commodities_served: prev.commodities_served.includes(value)
        ? prev.commodities_served.filter(c => c !== value)
        : [...prev.commodities_served, value]
    }));
  };

  const getCommodityBadge = (commodity) => {
    const found = commodities.find(c => c.value === commodity);
    if (!found) return <Badge variant="outline" className="rounded-none">{commodity}</Badge>;
    const Icon = found.icon;
    return (
      <Badge variant="outline" className={`rounded-none ${found.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {found.label}
      </Badge>
    );
  };

  // Filtered data
  const filteredVendors = vendors.filter(v => {
    const matchesSearch = !searchQuery || 
      v.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.account_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCommodity = commodityFilter === 'all' || v.commodity === commodityFilter;
    return matchesSearch && matchesCommodity;
  });

  const filteredAccounts = utilityAccounts.filter(a => {
    const matchesSearch = !searchQuery || 
      a.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.account_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.service_address?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCommodity = commodityFilter === 'all' || a.commodity === commodityFilter;
    const matchesStatus = caStatusFilter === 'all' || (a.status || 'active') === caStatusFilter;
    const matchesProperty = propertyFilter === 'all' || a.linked_location_id === propertyFilter;
    return matchesSearch && matchesCommodity && matchesStatus && matchesProperty;
  });

  const filteredUnitAccounts = unitAccounts.filter(a => {
    const matchesSearch = !searchQuery || 
      a.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.account_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.service_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.unit_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCommodity = commodityFilter === 'all' || a.commodity === commodityFilter;
    const matchesStatus = unitStatusFilter === 'all' || (a.occupancy_status || 'occupied') === unitStatusFilter;
    const matchesProperty = propertyFilter === 'all' || a.linked_location_id === propertyFilter;
    return matchesSearch && matchesCommodity && matchesStatus && matchesProperty;
  });

  // Group vendors by vendor_id (fall back to vendor_name so unlinked
  // AI-migration accounts group by their real name instead of one 'Unknown').
  const groupedVendors = {};
  vendors.forEach(va => {
    const gkey = va.vendor_id || `name:${va.vendor_name || 'Unknown'}`;
    if (!groupedVendors[gkey]) {
      groupedVendors[gkey] = {
        vendor_id: va.vendor_id,
        vendor_name: va.vendor_name,
        vendor_code: va.vendor_code,
        accounts: []
      };
    }
    groupedVendors[gkey].accounts.push(va);
  });

  // Also include vendors from utility accounts
  utilityAccounts.forEach(ua => {
    if (ua.vendor_id && !groupedVendors[ua.vendor_id]) {
      const vendorInfo = availableVendors.find(v => v.id === ua.vendor_id);
      groupedVendors[ua.vendor_id] = {
        vendor_id: ua.vendor_id,
        vendor_name: ua.vendor_name || vendorInfo?.vendor_name || 'Unknown',
        vendor_code: vendorInfo?.vendor_code || null,
        bp_supplier_id: vendorInfo?.bp_supplier_id || null,
        accounts: []
      };
    }
  });

  // Merge org vendor settings into grouped vendors
  Object.keys(groupedVendors).forEach(vid => {
    if (orgVendorSettingsMap[vid]) {
      groupedVendors[vid].org_settings = orgVendorSettingsMap[vid];
      // Use created_at from org settings if available
      if (!groupedVendors[vid].created_at && orgVendorSettingsMap[vid].created_at) {
        groupedVendors[vid].created_at = orgVendorSettingsMap[vid].created_at;
      }
    }
  });

  const vendorList = Object.values(groupedVendors);

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Vendor Management</h1>
              <p className="text-sm text-muted-foreground">
                Manage vendors and utility accounts
              </p>
            </div>
            <NotesIcon pageKey="vendors" pageLabel="Vendor Management" accountId={currentAccount?.id} />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={activeTab === 'vendors' ? handleExportVendorAccounts : handleExportUtilityAccounts}
              className="rounded-none"
              data-testid="export-csv-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowUploadModal(true)}
              className="rounded-none"
              data-testid="upload-accounts-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Accounts
            </Button>
            <Button
              onClick={() => setShowAddVendorModal(true)}
              className="rounded-none"
              data-testid="add-vendor-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Vendor
            </Button>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={(tab) => {
          setActiveTab(tab);
          setSearchQuery('');
          setCommodityFilter('all');
          setPropertyFilter('all');
          setCaStatusFilter('all');
          setUnitStatusFilter('all');
        }} className="w-full">
          <TabsList className="rounded-none">
            <TabsTrigger value="vendors" className="rounded-none" data-testid="vendors-tab">
              Vendors ({vendorList.length})
            </TabsTrigger>
            <TabsTrigger value="accounts" className="rounded-none" data-testid="accounts-tab">
              Utility Accounts (CA) ({totalAccountsCount})
            </TabsTrigger>
            <TabsTrigger value="unit-accounts" className="rounded-none" data-testid="unit-accounts-tab">
              Unit Accounts ({totalUnitAccountsCount})
            </TabsTrigger>
            <TabsTrigger value="credentials" className="rounded-none" data-testid="credentials-tab">
              <Key className="h-4 w-4 mr-1" />
              Portal Credentials ({portalCredentials.length})
            </TabsTrigger>
            <TabsTrigger value="collector-bots" className="rounded-none" data-testid="collector-bots-tab-trigger">
              <Bot className="h-4 w-4 mr-1" />
              Portal Collector Bots
            </TabsTrigger>
          </TabsList>

          {/* Filters */}
          <div className="flex items-center gap-4 mt-4 flex-wrap">
            <div className="relative flex-1 max-w-sm min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by account #, supplier, address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-none"
                data-testid="search-input"
              />
            </div>
            <Select value={commodityFilter} onValueChange={setCommodityFilter}>
              <SelectTrigger className="w-40 rounded-none">
                <SelectValue placeholder="Commodity" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All Commodities</SelectItem>
                {commodities.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(activeTab === 'unit-accounts') ? (
              <Select value={unitStatusFilter} onValueChange={setUnitStatusFilter}>
                <SelectTrigger className="w-32 rounded-none" data-testid="status-filter">
                  <SelectValue placeholder="Occupancy" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="vacant">Vacant</SelectItem>
                  <SelectItem value="occupied">Occupied</SelectItem>
                </SelectContent>
              </Select>
            ) : (activeTab === 'accounts') ? (
              <Select value={caStatusFilter} onValueChange={setCaStatusFilter}>
                <SelectTrigger className="w-32 rounded-none" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            ) : null}
            <Select value={propertyFilter} onValueChange={setPropertyFilter}>
              <SelectTrigger className="w-44 rounded-none" data-testid="property-filter">
                <SelectValue placeholder="Property" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All Properties</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Vendors Tab */}
          <TabsContent value="vendors" className="mt-4">
            <div className="border rounded-none">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Vendor</th>
                    <th className="text-left p-3 font-medium">Code</th>
                    <th className="text-left p-3 font-medium">BP Supplier</th>
                    <th className="text-left p-3 font-medium">Payment Method</th>
                    <th className="text-left p-3 font-medium">Accounts</th>
                    <th className="text-left p-3 font-medium">Date Added</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {initializing || loading ? (
                    <tr>
                      <td colSpan={7} className="text-center p-8 text-muted-foreground">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : vendorList.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center p-8 text-muted-foreground">
                        <Building className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        No vendors found. Add a vendor to get started.
                      </td>
                    </tr>
                  ) : (
                    vendorList.map(vendor => (
                      <tr key={vendor.vendor_id} className="border-t hover:bg-muted/30">
                        <td className="p-3">
                          <div className="font-medium">{vendor.vendor_name}</div>
                        </td>
                        <td className="p-3">
                          <span className="font-mono text-sm">{vendor.vendor_code || '-'}</span>
                        </td>
                        <td className="p-3">
                          <span className="font-mono text-xs text-muted-foreground">{vendor.bp_supplier_id || '-'}</span>
                        </td>
                        <td className="p-3">
                          {vendor.org_settings?.preferred_payment_method ? (
                            <Badge variant="outline" className="rounded-none text-xs capitalize">
                              {vendor.org_settings.preferred_payment_method.replace('_', ' ')}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge variant="secondary" className="rounded-none">
                            {vendor.accounts?.length || 0} accounts
                          </Badge>
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-muted-foreground">
                            {vendor.created_at ? new Date(vendor.created_at).toLocaleDateString() : (vendor.synced_at ? new Date(vendor.synced_at).toLocaleDateString() : '-')}
                          </span>
                        </td>
                        <td className="p-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openVendorDetails(vendor.vendor_id)}
                            data-testid={`view-vendor-${vendor.vendor_id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Utility Accounts Tab */}
          <TabsContent value="accounts" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddAccountModal(true)}
                className="rounded-none"
                data-testid="add-account-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Account
              </Button>
            </div>
            <div className="border rounded-none overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Vendor</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Account #</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Frequency</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Commodity</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">GL Code</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Status</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Period</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Service Address</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Bot State</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Linked To</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {initializing || loading ? (
                      <tr>
                        <td colSpan={11} className="text-center p-8 text-muted-foreground">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                          Loading...
                        </td>
                      </tr>
                    ) : filteredAccounts.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="text-center p-8 text-muted-foreground">
                          <Gauge className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          No utility accounts found. Upload a CSV or add an account.
                        </td>
                      </tr>
                    ) : (
                      filteredAccounts.map(account => (
                        <tr key={account.id} className="border-t hover:bg-muted/30">
                          <td className="p-3">
                            <div className="font-medium text-sm max-w-[160px] truncate" title={account.vendor_name}>{account.vendor_name}</div>
                            {account.meter_number && (
                              <div className="text-xs text-muted-foreground font-mono">Meter: {account.meter_number}</div>
                            )}
                          </td>
                          <td className="p-3 font-mono text-xs whitespace-nowrap">{account.account_number}</td>
                          <td className="p-3 text-xs whitespace-nowrap">{account.frequency || '-'}</td>
                          <td className="p-3">{getCommodityBadge(account.commodity)}</td>
                          <td className="p-3 font-mono text-xs whitespace-nowrap" data-testid={`account-gl-code-${account.id}`}>
                            {account.gl_code
                              ? <span className="text-foreground">{account.gl_code}</span>
                              : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="p-3">
                            <Badge variant={account.status === 'active' ? 'default' : 'secondary'} className="rounded-none text-xs">
                              {(account.status || 'active').charAt(0).toUpperCase() + (account.status || 'active').slice(1)}
                            </Badge>
                          </td>
                          <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                            {account.account_start ? new Date(account.account_start).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'2-digit'}) : '–'}
                            {' – '}
                            {(() => {
                              if (!account.account_end) return 'Ongoing';
                              const endYear = new Date(account.account_end).getFullYear();
                              return endYear >= 2029 ? 'Ongoing' : new Date(account.account_end).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'2-digit'});
                            })()}
                          </td>
                          <td className="p-3">
                            <div className="text-xs max-w-[200px] truncate" title={[account.service_address, account.service_city, account.service_state].filter(Boolean).join(', ')}>
                              {account.service_address || ''}
                              {(account.service_city || account.service_state) && (
                                <span className="text-muted-foreground"> {[account.service_city, account.service_state].filter(Boolean).join(', ')}</span>
                              )}
                              {!account.service_address && !account.service_city ? '-' : ''}
                            </div>
                          </td>
                          <td className="p-3" data-testid={`bot-state-${account.id}`}>
                            {account.bot_state === 'active' ? (
                              <Badge
                                variant="outline"
                                className="rounded-none text-xs border-emerald-500 text-emerald-700 gap-1"
                                title={`Automated — BillSource: ${account.bot_bill_source || 'Portal'}${account.bot_id ? ` · Bot #${account.bot_id}` : ''}`}
                              >
                                <Bot className="h-3 w-3" /> Automated
                              </Badge>
                            ) : (
                              <Badge
                                variant="secondary"
                                className="rounded-none text-xs text-slate-600"
                                title={account.bot_bill_source ? `Not automated — BillSource: ${account.bot_bill_source}` : 'Not automated — no bot account'}
                              >
                                Not Automated
                              </Badge>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {account.location_name && (
                                <Badge variant="outline" className="rounded-none text-xs truncate max-w-[120px]" title={account.location_name}>
                                  {account.location_name}
                                </Badge>
                              )}
                              {!account.location_name && !account.space_name && (
                                <span className="text-muted-foreground text-xs">Not linked</span>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => openLinkDialog(account)}
                                title="Link meters & identifier"
                                data-testid={`link-account-${account.id}`}
                              >
                                <Link className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => openEditAccount(account)}
                                data-testid={`edit-account-${account.id}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive"
                                onClick={() => { setSelectedAccount(account); setShowDeleteConfirm(true); }}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* Lazy loading indicator + Load More */}
              {loadingMoreAccounts && (
                <div className="flex items-center justify-center py-3 border-t">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                  <span className="text-xs text-muted-foreground">Loading more...</span>
                </div>
              )}
              {hasMoreAccounts && !loadingMoreAccounts && (
                <div className="flex items-center justify-center py-3 border-t">
                  <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={loadMoreAccounts}>
                    Load More ({utilityAccounts.length} of {totalAccountsCount})
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-2">
              <p className="text-xs text-muted-foreground">Showing {filteredAccounts.length} of {totalAccountsCount} account(s)</p>
            </div>
          </TabsContent>

          {/* Unit Accounts Tab */}
          <TabsContent value="unit-accounts" className="mt-4">
            <div className="border rounded-none overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Supplier</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Account #</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Commodity</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Unit</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Status</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Last Bill</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Service Address</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Property</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {initializing || loading ? (
                      <tr>
                        <td colSpan={9} className="text-center p-8 text-muted-foreground">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                          Loading...
                        </td>
                      </tr>
                    ) : filteredUnitAccounts.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center p-8 text-muted-foreground">
                          <Building className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          No vacant unit accounts found. Run the Unit Accounts Sync from the Job Queue.
                        </td>
                      </tr>
                    ) : (
                      filteredUnitAccounts.map(account => (
                        <tr key={account.id} className="border-t hover:bg-muted/30" data-testid={`unit-account-row-${account.id}`}>
                          <td className="p-3">
                            <div className="font-medium text-sm max-w-[160px] truncate" title={account.vendor_name}>{account.vendor_name || '-'}</div>
                          </td>
                          <td className="p-3 font-mono text-xs whitespace-nowrap">{account.account_number}</td>
                          <td className="p-3">{getCommodityBadge(account.commodity)}</td>
                          <td className="p-3">
                            <span className="font-mono text-xs">{account.space_unit_number || account.unit_number || '-'}</span>
                            {account.bp_unit_id && (
                              <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[100px]" title={account.bp_unit_id}>
                                {account.bp_unit_id.slice(0, 8)}...
                              </div>
                            )}
                          </td>
                          <td className="p-3">
                            <Badge 
                              variant="outline" 
                              className={`rounded-none text-xs ${(account.occupancy_status || 'occupied') === 'vacant' ? 'border-amber-500 text-amber-700 bg-amber-50' : 'border-green-500 text-green-700 bg-green-50'}`}
                            >
                              {(account.occupancy_status || 'occupied') === 'vacant' ? 'Vacant' : 'Occupied'}
                            </Badge>
                          </td>
                          <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                            {account.last_bill_date ? new Date(account.last_bill_date).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'2-digit'}) : '-'}
                            {account.last_bill_amount > 0 && (
                              <div className="text-[10px] font-mono">${account.last_bill_amount.toFixed(2)}</div>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="text-xs max-w-[180px] truncate" title={[account.service_address, account.service_city, account.service_state].filter(Boolean).join(', ')}>
                              {account.service_address || ''}
                              {(account.service_city || account.service_state) && (
                                <span className="text-muted-foreground"> {[account.service_city, account.service_state].filter(Boolean).join(', ')}</span>
                              )}
                              {!account.service_address && !account.service_city ? '-' : ''}
                            </div>
                          </td>
                          <td className="p-3">
                            {account.location_name ? (
                              <Badge variant="outline" className="rounded-none text-xs truncate max-w-[120px]" title={account.location_name}>
                                {account.location_name}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => openLinkDialog(account)}
                              title="Link meters & identifier"
                              data-testid={`link-unit-account-${account.id}`}
                            >
                              <Link className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {loadingUnitAccounts && (
                <div className="flex items-center justify-center py-3 border-t">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                  <span className="text-xs text-muted-foreground">Loading more...</span>
                </div>
              )}
              {!loadingUnitAccounts && hasMoreUnitAccounts && unitAccounts.length > 0 && (
                <div className="flex justify-center py-3 border-t">
                  <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={loadMoreUnitAccounts}>
                    Load More ({unitAccounts.length} of {totalUnitAccountsCount})
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-2">
              <p className="text-xs text-muted-foreground">Showing {filteredUnitAccounts.length} of {totalUnitAccountsCount} unit account(s)</p>
            </div>
          </TabsContent>

          {/* Portal Credentials Tab */}
          <TabsContent value="credentials" className="mt-4">
            <div className="flex justify-end mb-4 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { resetCredentialForm(); setShowAddCredentialModal(true); }}
                className="rounded-none"
                data-testid="add-credential-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Credential
              </Button>
            </div>
            <TooltipProvider>
            <div className="border rounded-none overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Account Name</th>
                    <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Supplier (Collector)</th>
                    <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Status</th>
                    <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Username</th>
                    <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Account #</th>
                    <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Last Collected</th>
                    <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Next Expected</th>
                    <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">
                      <span className="inline-flex items-center gap-1">
                        Auto-Collect
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" className="text-muted-foreground" data-testid="auto-collect-info">
                              <Info className="h-3 w-3" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs leading-relaxed">
                            When on, the collector picks your 2 latest bills, learns the billing frequency from their issue dates, predicts the next bill's issue date and auto-collects it. If no bills exist yet, it runs every day until it can build the schedule.
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    </th>
                    <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {initializing || loading ? (
                    <tr>
                      <td colSpan={9} className="text-center p-8 text-muted-foreground">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : portalCredentials.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center p-8 text-muted-foreground">
                        <Key className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        No portal credentials found. Add credentials to access vendor portals.
                      </td>
                    </tr>
                  ) : (
                    portalCredentials.map(cred => {
                      const canCollect = !!cred.collector_supplier;
                      const isCollecting = collectingId === cred.id;
                      const acctCount = cred.collector_supplier
                        ? (collectorCounts[cred.id] ?? 0)
                        : 0;
                      const isExpanded = expandedCredId === cred.id;
                      const panel = collectorAccts[cred.id];
                      return (
                      <Fragment key={cred.id}>
                      <tr className="border-t hover:bg-muted/30" data-testid={`credential-row-${cred.id}`}>
                        <td className="p-3">{cred.account_name || '-'}</td>
                        <td className="p-3">
                          {cred.collector_supplier_name ? (
                            <Badge variant="outline" className="rounded-none text-xs"><Bot className="h-3 w-3 mr-1" />{cred.collector_supplier_name}</Badge>
                          ) : (<span className="text-xs text-muted-foreground">Not set</span>)}
                        </td>
                        <td className="p-3">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1.5" data-testid={`credential-connectivity-${cred.id}`}>
                                <span className={`h-2.5 w-2.5 rounded-full ${connMeta(cred.connectivity_status).dot}`} />
                                <span className="text-xs text-muted-foreground">{connMeta(cred.connectivity_status).label}</span>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs">
                              {cred.connectivity_reason || connMeta(cred.connectivity_status).tip}
                            </TooltipContent>
                          </Tooltip>
                        </td>
                        <td className="p-3 font-mono text-xs">{cred.username || '-'}</td>
                        <td className="p-3">
                          {cred.collector_supplier ? (
                            <button
                              type="button"
                              onClick={() => toggleExpandCredential(cred)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                              data-testid={`credential-accounts-count-${cred.id}`}
                            >
                              <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                              {acctCount} account{acctCount === 1 ? '' : 's'}
                            </button>
                          ) : (
                            <span className="font-mono text-xs text-muted-foreground">{cred.account_number || '\u2014'}</span>
                          )}
                        </td>
                        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{fmtColDate(cred.last_collected_issue_date)}</td>
                        <td className="p-3 text-xs whitespace-nowrap">
                          {cred.next_expected_date ? (
                            <span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3 text-amber-600" />{fmtColDate(cred.next_expected_date)}</span>
                          ) : <span className="text-muted-foreground">{'\u2014'}</span>}
                        </td>
                        <td className="p-3">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">
                                <Switch
                                  checked={!!cred.schedule_enabled}
                                  disabled={!canCollect}
                                  onCheckedChange={(v) => handleToggleSchedule(cred, v)}
                                  data-testid={`credential-schedule-${cred.id}`}
                                />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs">
                              {canCollect ? 'Auto-collect: picks 2 latest bills, learns the frequency, predicts the next issue date and collects it automatically. Runs daily until a schedule can be built.' : 'Set a Supplier (Collector) first to enable auto-collect.'}
                            </TooltipContent>
                          </Tooltip>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1 items-center">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-none h-8"
                              disabled={!canCollect || isCollecting}
                              onClick={() => handleRunCollect(cred)}
                              data-testid={`credential-collect-${cred.id}`}
                            >
                              {isCollecting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                              Collect
                            </Button>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    disabled={!canCollect || isCollecting}
                                    onClick={() => handleRunCollect(cred, true)}
                                    data-testid={`credential-reimport-${cred.id}`}
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs text-xs">
                                Force re-import: re-pull &amp; re-ingest bills even if they were already imported (bypasses duplicate skip).
                              </TooltipContent>
                            </Tooltip>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEditCredential(cred)}
                              data-testid={`credential-edit-${cred.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => { setSelectedCredential(cred); setShowDeleteCredentialConfirm(true); }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-muted/20" data-testid={`credential-accounts-panel-${cred.id}`}>
                          <td colSpan={9} className="p-0">
                            <div className="p-3">
                              {panel?.loading ? (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
                                  <RefreshCw className="h-4 w-4 animate-spin" /> Loading accounts…
                                </div>
                              ) : panel?.error ? (
                                <div className="text-sm text-muted-foreground py-4 text-center">
                                  Couldn't load accounts for this supplier.
                                </div>
                              ) : (panel?.accounts || []).length === 0 ? (
                                <div className="text-sm text-muted-foreground py-4 text-center">
                                  No accounts are mapped to this supplier{'\u2019'}s portal for this organization.
                                </div>
                              ) : (
                                <div className="border rounded-none overflow-x-auto bg-background">
                                  <table className="w-full text-sm" data-testid={`collector-accounts-table-${cred.id}`}>
                                    <thead className="bg-muted/50">
                                      <tr>
                                        <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Supplier</th>
                                        <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Account Number</th>
                                        <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">NMI</th>
                                        <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Site</th>
                                        <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Utility Type</th>
                                        <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Frequency</th>
                                        <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Status</th>
                                        <th className="text-right p-2 font-medium text-xs uppercase tracking-wide">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(panel?.accounts || []).map((acct) => {
                                        const active = acct.status === 'active';
                                        return (
                                          <tr key={acct.account_number} className="border-t" data-testid={`collector-account-row-${cred.id}-${acct.account_number}`}>
                                            <td className="p-2">{acct.supplier || cred.collector_supplier_name || cred.collector_supplier}</td>
                                            <td className="p-2 font-mono text-xs">{acct.account_number}</td>
                                            <td className="p-2 text-xs">{acct.nmi || '\u2014'}</td>
                                            <td className="p-2">{acct.site || '\u2014'}</td>
                                            <td className="p-2">
                                              {acct.utility_type ? (
                                                <Badge variant="outline" className="rounded-none text-xs">{acct.utility_type}</Badge>
                                              ) : '\u2014'}
                                            </td>
                                            <td className="p-2 text-xs">{acct.frequency || '\u2014'}</td>
                                            <td className="p-2">
                                              <span className="inline-flex items-center gap-1.5">
                                                <span className={`h-2 w-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                                                <span className="text-xs">{active ? 'Active' : 'Inactive'}</span>
                                              </span>
                                            </td>
                                            <td className="p-2 text-right">
                                              <Button
                                                size="sm"
                                                variant={active ? 'destructive' : 'default'}
                                                className="rounded-none h-7 text-xs"
                                                disabled
                                                title="Activate/Deactivate is not available yet"
                                                data-testid={`collector-account-toggle-${cred.id}-${acct.account_number}`}
                                              >
                                                {active ? 'Deactivate' : 'Activate'}
                                              </Button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            </TooltipProvider>
          </TabsContent>

          {/* Portal Collector Bots Tab */}
          <TabsContent value="collector-bots" className="mt-4">
            {currentAccount && <CollectorBotsTab accountId={currentAccount.id} />}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Vendor Modal */}
      <Sheet open={showAddVendorModal} onOpenChange={setShowAddVendorModal}>
        <SheetContent side="right" className="sm:max-w-[520px] w-[520px] flex flex-col p-0" data-testid="add-vendor-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle>Add New Vendor</SheetTitle>
            <SheetDescription>Add a supplier. Enter an account number to also create its first utility account.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleAddVendor} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Supplier Name *</Label>
                <Input className="rounded-none mt-1" value={vendorForm.vendor_name} onChange={(e) => setVendorForm({ ...vendorForm, vendor_name: e.target.value })} placeholder="e.g., Pacific Gas & Electric" required data-testid="vendor-name-input" />
              </div>
              <div>
                <Label>Support Phone</Label>
                <Input className="rounded-none mt-1" value={vendorForm.support_phone} onChange={(e) => setVendorForm({ ...vendorForm, support_phone: e.target.value })} placeholder="e.g., 1-800-743-5000" data-testid="add-vendor-support-phone" />
              </div>
              <div>
                <Label>Email</Label>
                <Input className="rounded-none mt-1" type="email" value={vendorForm.email} onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })} placeholder="billing@vendor.com" data-testid="add-vendor-email" />
              </div>
              <div>
                <Label>Account Number</Label>
                <Input className="rounded-none mt-1 font-mono" value={vendorForm.account_number} onChange={(e) => setVendorForm({ ...vendorForm, account_number: e.target.value })} placeholder="e.g., ACC-123456" data-testid="add-vendor-account-number" />
              </div>
              <div>
                <Label>Commodity</Label>
                <Select value={vendorForm.account_commodity} onValueChange={(v) => setVendorForm({ ...vendorForm, account_commodity: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="add-vendor-account-commodity"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{accountCommodities.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={vendorForm.status} onValueChange={(v) => setVendorForm({ ...vendorForm, status: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="add-vendor-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Frequency</Label>
                <Input className="rounded-none mt-1" value={vendorForm.frequency} onChange={(e) => setVendorForm({ ...vendorForm, frequency: e.target.value })} placeholder="e.g., Monthly" data-testid="add-vendor-frequency" />
              </div>
              <div className="col-span-2">
                <Label>Bill Source</Label>
                <Input className="rounded-none mt-1" value={vendorForm.bill_source} onChange={(e) => setVendorForm({ ...vendorForm, bill_source: e.target.value })} placeholder="e.g., Portal, Email" data-testid="add-vendor-bill-source" />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Input className="rounded-none mt-1" value={vendorForm.service_address} onChange={(e) => setVendorForm({ ...vendorForm, service_address: e.target.value })} data-testid="add-vendor-address" />
              </div>
              <div>
                <Label>City</Label>
                <Input className="rounded-none mt-1" value={vendorForm.service_city} onChange={(e) => setVendorForm({ ...vendorForm, service_city: e.target.value })} data-testid="add-vendor-city" />
              </div>
              <div>
                <Label>State</Label>
                <Input className="rounded-none mt-1" value={vendorForm.service_state} onChange={(e) => setVendorForm({ ...vendorForm, service_state: e.target.value })} data-testid="add-vendor-state" />
              </div>
              <div>
                <Label>Country</Label>
                <Input className="rounded-none mt-1" value={vendorForm.service_country} onChange={(e) => setVendorForm({ ...vendorForm, service_country: e.target.value })} data-testid="add-vendor-country" />
              </div>
              <div>
                <Label>Post Code</Label>
                <Input className="rounded-none mt-1" value={vendorForm.service_zip} onChange={(e) => setVendorForm({ ...vendorForm, service_zip: e.target.value })} data-testid="add-vendor-postcode" />
              </div>
              <div>
                <Label>Account Start Date</Label>
                <Input type="date" className="rounded-none mt-1" value={vendorForm.account_start} onChange={(e) => setVendorForm({ ...vendorForm, account_start: e.target.value })} data-testid="add-vendor-account-start" />
              </div>
              <div>
                <Label>Account End Date</Label>
                <Input type="date" className="rounded-none mt-1" value={vendorForm.account_end} onChange={(e) => setVendorForm({ ...vendorForm, account_end: e.target.value })} data-testid="add-vendor-account-end" />
              </div>
              <div className="col-span-2">
                <Label>Account Name</Label>
                <Input className="rounded-none mt-1" value={vendorForm.account_name} onChange={(e) => setVendorForm({ ...vendorForm, account_name: e.target.value })} data-testid="add-vendor-account-name" />
              </div>
              <div className="col-span-2">
                <Label>Missing Bills — Expected Start Date</Label>
                <Input type="date" className="rounded-none mt-1" value={vendorForm.missing_bills_expected_start_date} onChange={(e) => setVendorForm({ ...vendorForm, missing_bills_expected_start_date: e.target.value })} data-testid="add-vendor-missing-start" />
                <p className="text-xs text-muted-foreground mt-1">When Missing-Bills tracking should begin. Defaults to the oldest bill on platform.</p>
              </div>
              <div className="col-span-2 flex items-center gap-2 border-t pt-3 mt-1">
                <Switch id="vendor-has-parent" checked={vendorForm.has_parent_account} onCheckedChange={(checked) => setVendorForm({ ...vendorForm, has_parent_account: !!checked, parent_account_number: checked ? vendorForm.parent_account_number : '' })} data-testid="add-vendor-has-parent" />
                <Label htmlFor="vendor-has-parent" className="cursor-pointer">Has parent account</Label>
              </div>
              {vendorForm.has_parent_account && (
                <div className="col-span-2">
                  <Label>Parent Account No</Label>
                  <Input className="rounded-none mt-1 font-mono" value={vendorForm.parent_account_number} onChange={(e) => setVendorForm({ ...vendorForm, parent_account_number: e.target.value })} data-testid="add-vendor-parent-number" />
                </div>
              )}
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-background">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => { setShowAddVendorModal(false); resetVendorForm(); }}>Cancel</Button>
              <Button type="submit" className="rounded-none" disabled={saving} data-testid="save-add-vendor-btn">{saving ? 'Adding...' : 'Add Vendor'}</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Vendor Detail Modal */}
      <Dialog open={showVendorDetailModal} onOpenChange={setShowVendorDetailModal}>
        <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              {vendorDetails?.vendor?.vendor_name || 'Vendor Details'}
            </DialogTitle>
          </DialogHeader>
          
          {vendorDetails && (
            <div className="space-y-6 py-4">
              {/* Vendor Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>PMS Alias Vendor Name</Label>
                  <Input
                    className="rounded-none mt-1"
                    value={orgSettingsForm.pms_alias_name}
                    onChange={(e) => setOrgSettingsForm({ ...orgSettingsForm, pms_alias_name: e.target.value })}
                    placeholder="Name used in your PMS"
                  />
                </div>
                <div>
                  <Label>PMS Vendor Code</Label>
                  <Input
                    className="rounded-none mt-1 font-mono uppercase"
                    value={orgSettingsForm.pms_vendor_code}
                    onChange={(e) => setOrgSettingsForm({ ...orgSettingsForm, pms_vendor_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., PGE001"
                  />
                </div>
                <div>
                  <Label>Preferred Payment Method</Label>
                  <Select 
                    value={orgSettingsForm.preferred_payment_method} 
                    onValueChange={(v) => setOrgSettingsForm({ ...orgSettingsForm, preferred_payment_method: v })}
                  >
                    <SelectTrigger className="rounded-none mt-1" data-testid="vendor-payment-method">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="ach">ACH</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                      <SelectItem value="wire">Wire Transfer</SelectItem>
                      <SelectItem value="auto">Auto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* ACH Details — shown when ACH selected */}
              {orgSettingsForm.preferred_payment_method === 'ach' && (
                <div className="space-y-3 p-3 border border-blue-200 bg-blue-50/30 rounded-none">
                  {achLocked && (achMask?.has_routing || achMask?.has_account) ? (
                    /* LOCK SCREEN — saved banking is hidden until OTP-verified */
                    <div className="bg-amber-50/50 border border-amber-200 rounded-none p-4">
                      <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-none bg-amber-100 border border-amber-300 flex items-center justify-center shrink-0">
                          <Key className="h-4 w-4 text-amber-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm">Banking saved · locked</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            ACH banking is encrypted at rest. Re-verify your account
                            (we'll email a 6-digit code) to view or modify.
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                            <div>
                              <span className="text-muted-foreground">Routing:</span>{' '}
                              <span className="font-mono">
                                {achMask?.has_routing ? `••••${achMask?.routing_last4 || '••'}` : '—'}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Account:</span>{' '}
                              <span className="font-mono">
                                {achMask?.has_account ? `••••${achMask?.account_last4 || '••'}` : '—'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          className="rounded-none shrink-0"
                          onClick={() => { setAchRevealOpen(true); setAchRevealStep('idle'); }}
                          data-testid="vendor-ach-unlock-btn"
                        >
                          <Key className="h-3 w-3 mr-1" /> Verify to view / modify
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* EDITABLE — first-time setup OR post-verify */
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>ACH Routing Number</Label>
                          <Input
                            className="rounded-none mt-1 font-mono"
                            value={orgSettingsForm.ach_routing_number || ''}
                            onChange={e => {
                              setOrgSettingsForm({ ...orgSettingsForm, ach_routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) });
                              setAchVerifyResult(null);
                            }}
                            placeholder="9-digit routing"
                            maxLength={9}
                            data-testid="vendor-ach-routing"
                          />
                        </div>
                        <div>
                          <Label>ACH Account Number</Label>
                          <Input
                            className="rounded-none mt-1 font-mono"
                            value={orgSettingsForm.ach_account_number || ''}
                            onChange={e => {
                              setOrgSettingsForm({ ...orgSettingsForm, ach_account_number: e.target.value.replace(/\D/g, '') });
                              setAchVerifyResult(null);
                            }}
                            placeholder="Account number"
                            data-testid="vendor-ach-account"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-3 pt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="rounded-none"
                          onClick={handleVerifyAch}
                          disabled={achVerifying || !orgSettingsForm.ach_routing_number || !orgSettingsForm.ach_account_number}
                          data-testid="vendor-ach-verify-btn"
                        >
                          {achVerifying ? (
                            <><Loader2 className="h-3 w-3 mr-2 animate-spin" /> Verifying…</>
                          ) : (
                            <><ShieldCheck className="h-3 w-3 mr-2" /> Verify ACH details</>
                          )}
                        </Button>
                        {achVerifyResult && (
                          <Badge
                            variant="outline"
                            className={`rounded-none ${achVerifyResult.success
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-rose-50 text-rose-700 border-rose-200'}`}
                            data-testid="vendor-ach-verify-result"
                          >
                            {achVerifyResult.success ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                            {achVerifyResult.success ? 'Verified' : 'Failed'}
                          </Badge>
                        )}
                        {!achVerifyResult && (
                          <span className="text-xs text-muted-foreground">
                            ABA check-digit + account format must pass before saving
                          </span>
                        )}
                      </div>

                      {achVerifyResult?.steps && (
                        <div className="text-xs space-y-1 pl-1 mt-2">
                          {achVerifyResult.steps.map((s, i) => (
                            <div key={i} className="flex items-start gap-2">
                              {s.ok
                                ? <CheckCircle className="h-3 w-3 text-emerald-600 mt-0.5 shrink-0" />
                                : <XCircle className="h-3 w-3 text-rose-600 mt-0.5 shrink-0" />}
                              <span className={s.ok ? 'text-emerald-700' : 'text-rose-700'}>
                                <strong>{s.label}:</strong> {s.message}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Linked Accounts Table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">Linked Utility Accounts ({vendorDetails.linked_accounts?.length || 0})</h3>
                </div>
                <div className="border rounded-none max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="text-left p-2">Account #</th>
                        <th className="text-left p-2">Account Name</th>
                        <th className="text-left p-2">Commodity</th>
                        <th className="text-left p-2">Property</th>
                        <th className="text-left p-2">Space</th>
                        <th className="text-left p-2">Meter</th>
                        <th className="text-left p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendorDetails.linked_accounts?.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center p-4 text-muted-foreground">
                            No accounts linked to this vendor
                          </td>
                        </tr>
                      ) : (
                        vendorDetails.linked_accounts?.map(acc => (
                          <tr key={acc.id} className="border-t hover:bg-muted/30">
                            <td className="p-2 font-mono">{acc.account_number}</td>
                            <td className="p-2">{acc.account_name || '-'}</td>
                            <td className="p-2">{getCommodityBadge(acc.commodity)}</td>
                            <td className="p-2">{acc.location_name || '-'}</td>
                            <td className="p-2">{acc.space_name || '-'}</td>
                            <td className="p-2">{acc.meter_display || '-'}</td>
                            <td className="p-2">
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => { setShowVendorDetailModal(false); openEditAccount(acc); }}
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-destructive"
                                  onClick={() => { setSelectedAccount(acc); setShowDeleteConfirm(true); }}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" className="rounded-none" onClick={() => setShowVendorDetailModal(false)}>
              Close
            </Button>
            <Button
              className="rounded-none"
              onClick={handleSaveOrgSettings}
              disabled={
                saving
                || (orgSettingsForm.preferred_payment_method === 'ach'
                    && achLocked
                    && (achMask?.has_routing || achMask?.has_account))
                || (orgSettingsForm.preferred_payment_method === 'ach'
                    && !achLocked
                    && !achVerifyResult?.success
                    && !!orgSettingsForm.ach_routing_number)
              }
              data-testid="vendor-orgsettings-save"
              title={
                orgSettingsForm.preferred_payment_method === 'ach' && achLocked
                  ? 'Unlock banking with OTP first'
                  : (orgSettingsForm.preferred_payment_method === 'ach' && !achVerifyResult?.success
                     ? 'Click "Verify ACH details" first'
                     : '')
              }
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor ACH Banking — OTP Reveal Modal */}
      <Dialog open={achRevealOpen} onOpenChange={(o) => (o ? setAchRevealOpen(true) : closeAchReveal())}>
        <DialogContent className="rounded-none max-w-md" data-testid="vendor-ach-reveal-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-emerald-600" />
              Verify identity to view or modify
            </DialogTitle>
            <DialogDescription>
              Saved vendor ACH banking is encrypted at rest. Enter the 6-digit code
              we've emailed to your registered address to unlock the routing and
              account fields.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {achRevealStep === 'idle' ? (
              <Button
                data-testid="vendor-ach-reveal-send-otp"
                className="rounded-none w-full font-bold uppercase"
                onClick={startAchReveal}
                disabled={achRevealSending}
              >
                {achRevealSending ? 'Sending…' : 'Email me a verification code'}
              </Button>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                    Verification Code
                  </Label>
                  <Input
                    data-testid="vendor-ach-reveal-code-input"
                    className="rounded-none font-mono text-lg tracking-[0.4em]"
                    placeholder="000000"
                    maxLength={6}
                    value={achRevealCode}
                    onChange={(e) => setAchRevealCode(e.target.value.replace(/\D/g, ''))}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Code expires in 5 minutes. 5 attempts max.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid="vendor-ach-reveal-submit"
                    className="rounded-none flex-1 font-bold uppercase"
                    onClick={submitAchReveal}
                    disabled={achRevealSending || achRevealCode.length !== 6}
                  >
                    {achRevealSending ? 'Verifying…' : 'Verify & unlock'}
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-none"
                    onClick={startAchReveal}
                    disabled={achRevealSending}
                    title="Resend code"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Account Side Panel */}
      <Sheet open={showAddAccountModal} onOpenChange={setShowAddAccountModal}>
        <SheetContent side="right" className="sm:max-w-[520px] w-[520px] flex flex-col p-0" data-testid="add-account-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle>Add Utility Account</SheetTitle>
            <SheetDescription>Add a new utility account. It will auto-link to properties/meters if a match is found.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleAddAccount} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Vendor *</Label>
                <Select value={accountForm.vendor_id} onValueChange={(v) => setAccountForm({ ...accountForm, vendor_id: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-60">{availableVendors.map(v => (<SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input className="rounded-none mt-1 font-mono" value={accountForm.account_number} onChange={(e) => setAccountForm({ ...accountForm, account_number: e.target.value })} placeholder="e.g., 1234567890" required data-testid="add-account-number-input" />
              </div>
              <div>
                <Label>Account Name (Bill To)</Label>
                <Input className="rounded-none mt-1" value={accountForm.account_name} onChange={(e) => setAccountForm({ ...accountForm, account_name: e.target.value })} placeholder="Property or company name" data-testid="add-account-name-input" />
              </div>
              <div>
                <Label>Commodity</Label>
                <Select value={accountForm.commodity} onValueChange={(v) => setAccountForm({ ...accountForm, commodity: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{commodities.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={accountForm.status || 'active'} onValueChange={(v) => setAccountForm({ ...accountForm, status: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="add-account-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Account Start</Label>
                <Input type="date" className="rounded-none mt-1" value={accountForm.account_start} onChange={(e) => setAccountForm({ ...accountForm, account_start: e.target.value })} data-testid="add-account-start" />
              </div>
              <div>
                <Label>Account End</Label>
                <Input type="date" className="rounded-none mt-1" value={accountForm.account_end} onChange={(e) => setAccountForm({ ...accountForm, account_end: e.target.value })} data-testid="add-account-end" />
                <p className="text-xs text-muted-foreground mt-1">Leave blank for ongoing</p>
              </div>
              <div className="col-span-2 border-t pt-3 mt-1">
                <Label className="flex items-center gap-1">Missing Bills — Expected Start Date</Label>
                <Input type="date" className="rounded-none mt-1" value={accountForm.missing_bills_expected_start_date} onChange={(e) => setAccountForm({ ...accountForm, missing_bills_expected_start_date: e.target.value })} data-testid="add-account-missing-start" />
                <p className="text-xs text-muted-foreground mt-1">When Missing-Bills tracking should begin. Defaults to the oldest bill on platform.</p>
              </div>
              <div className="col-span-2">
                <Label>Property</Label>
                <Select value={accountForm.linked_location_id || 'none'} onValueChange={(v) => setAccountForm({ ...accountForm, linked_location_id: v === 'none' ? '' : v, linked_space_id: '', linked_meter_id: '' })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="add-account-property"><SelectValue placeholder="Select property to link" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-60">
                    <SelectItem value="none">-- Not Linked --</SelectItem>
                    {locations.map(loc => (<SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Or leave blank to auto-link from the service address below.</p>
              </div>
              <div className="col-span-2">
                <Label>Service Address</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_address} onChange={(e) => setAccountForm({ ...accountForm, service_address: e.target.value })} placeholder="123 Main St" />
              </div>
              <div>
                <Label>City</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_city} onChange={(e) => setAccountForm({ ...accountForm, service_city: e.target.value })} />
              </div>
              <div>
                <Label>State</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_state} onChange={(e) => setAccountForm({ ...accountForm, service_state: e.target.value.toUpperCase() })} maxLength={2} />
              </div>
              <div>
                <Label>ZIP Code</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_zip} onChange={(e) => setAccountForm({ ...accountForm, service_zip: e.target.value })} />
              </div>
              <div>
                <Label>Meter Number</Label>
                <Input className="rounded-none mt-1 font-mono" value={accountForm.meter_number} onChange={(e) => setAccountForm({ ...accountForm, meter_number: e.target.value })} placeholder="For auto-linking" />
              </div>
              <div>
                <Label>BP Entity ID</Label>
                <Input className="rounded-none mt-1 font-mono" value={accountForm.bp_entity_id} onChange={(e) => setAccountForm({ ...accountForm, bp_entity_id: e.target.value })} placeholder="MySQL entity ID" />
              </div>
              <div>
                <Label>Frequency</Label>
                <Input className="rounded-none mt-1" value={accountForm.frequency} onChange={(e) => setAccountForm({ ...accountForm, frequency: e.target.value })} placeholder="e.g., Monthly" data-testid="add-account-frequency" />
              </div>
              <div>
                <Label>Bill Source</Label>
                <Input className="rounded-none mt-1" value={accountForm.bill_source} onChange={(e) => setAccountForm({ ...accountForm, bill_source: e.target.value })} placeholder="e.g., Portal, Email" data-testid="add-account-bill-source" />
              </div>
              <div className="col-span-2 flex items-center gap-2 border-t pt-3 mt-1">
                <Switch id="add-has-parent" checked={accountForm.has_parent_account} onCheckedChange={(checked) => setAccountForm({ ...accountForm, has_parent_account: !!checked, parent_account_number: checked ? accountForm.parent_account_number : '' })} data-testid="add-account-has-parent" />
                <Label htmlFor="add-has-parent" className="cursor-pointer">Has parent account number</Label>
              </div>
              {accountForm.has_parent_account && (
                <div className="col-span-2">
                  <Label>Parent Account No</Label>
                  <Input className="rounded-none mt-1 font-mono" value={accountForm.parent_account_number} onChange={(e) => setAccountForm({ ...accountForm, parent_account_number: e.target.value })} data-testid="add-account-parent-number" />
                </div>
              )}
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-background">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => { setShowAddAccountModal(false); resetAccountForm(); }}>Cancel</Button>
              <Button type="submit" className="rounded-none" disabled={saving} data-testid="save-add-account-btn">{saving ? 'Adding...' : 'Add Account'}</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* CSV Upload Dialog */}
      <Dialog open={showUploadModal} onOpenChange={(open) => { setShowUploadModal(open); if (!open) setUploadResults(null); }}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Upload Utility Accounts
            </DialogTitle>
            <DialogDescription>
              Upload a CSV to bulk import accounts. Vendors will be auto-created if not found, and accounts will be auto-linked to properties/meters.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Download Template */}
            <div className="border border-dashed border-border rounded-none p-4 bg-muted/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Download Template</p>
                  <p className="text-xs text-muted-foreground">CSV with required columns</p>
                </div>
                <Button variant="outline" size="sm" className="rounded-none" onClick={handleDownloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Template
                </Button>
              </div>
            </div>

            {/* Upload Area */}
            <div className="border border-dashed border-border rounded-none p-6 text-center bg-muted/10 hover:bg-muted/20 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                className="hidden"
                id="csv-upload"
                data-testid="csv-file-input"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {uploading ? 'Uploading...' : 'Click to upload CSV file'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  CSV files only
                </p>
              </label>
            </div>

            {/* Upload Results */}
            {uploadResults && (
              <div className="space-y-2">
                {(uploadResults.created > 0 || uploadResults.updated > 0) && (
                  <div className="bg-green-50 p-3 rounded-none">
                    <div className="flex items-center gap-2 text-green-600 mb-1">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">Success</span>
                    </div>
                    <div className="text-xs text-green-700 space-y-1">
                      <p>Created: {uploadResults.created}, Updated: {uploadResults.updated}</p>
                      <p>Auto-linked: {uploadResults.auto_linked} accounts</p>
                      {uploadResults.vendors_created > 0 && (
                        <p>New vendors created: {uploadResults.vendors_created} ({uploadResults.vendors_for_review} pending review)</p>
                      )}
                    </div>
                  </div>
                )}
                {uploadResults.errors?.length > 0 && (
                  <div className="bg-red-50 p-2 rounded-none">
                    <div className="flex items-center gap-2 text-red-600 mb-2">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">{uploadResults.errors.length} Error(s)</span>
                    </div>
                    <div className="max-h-32 overflow-y-auto">
                      {uploadResults.errors.slice(0, 5).map((err, i) => (
                        <p key={i} className="text-xs text-red-700">
                          {err.row ? `Row ${err.row}: ` : ''}{err.error}
                        </p>
                      ))}
                      {uploadResults.errors.length > 5 && (
                        <p className="text-xs text-red-500 mt-1">
                          ...and {uploadResults.errors.length - 5} more errors
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => { setShowUploadModal(false); setUploadResults(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Delete Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete account "{selectedAccount?.account_number}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" className="rounded-none" onClick={handleDeleteAccount} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Account Side Panel */}
      <Sheet open={showEditAccountModal} onOpenChange={(open) => { setShowEditAccountModal(open); if (!open) { setEditingAccount(null); resetAccountForm(); } }}>
        <SheetContent side="right" className="sm:max-w-[560px] w-[560px] flex flex-col p-0" data-testid="edit-account-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle>Edit Utility Account</SheetTitle>
            <SheetDescription>Update account details and manage property/space/meter linking.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleUpdateAccount} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Vendor *</Label>
                <Select value={accountForm.vendor_id} onValueChange={(v) => setAccountForm({ ...accountForm, vendor_id: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-60">{availableVendors.map(v => (<SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input className="rounded-none mt-1 font-mono" value={accountForm.account_number} onChange={(e) => setAccountForm({ ...accountForm, account_number: e.target.value })} required />
              </div>
              <div>
                <Label>Account Name (Bill To)</Label>
                <Input className="rounded-none mt-1" value={accountForm.account_name} onChange={(e) => setAccountForm({ ...accountForm, account_name: e.target.value })} placeholder="Property or company name" />
              </div>
              <div>
                <Label>Commodity</Label>
                <Select value={accountForm.commodity} onValueChange={(v) => setAccountForm({ ...accountForm, commodity: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{commodities.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={accountForm.status || 'active'} onValueChange={(v) => setAccountForm({ ...accountForm, status: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="edit-account-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Account Start</Label>
                <Input type="date" className="rounded-none mt-1" value={accountForm.account_start} onChange={(e) => setAccountForm({ ...accountForm, account_start: e.target.value })} data-testid="edit-account-start" />
              </div>
              <div>
                <Label>Account End</Label>
                <Input type="date" className="rounded-none mt-1" value={accountForm.account_end} onChange={(e) => setAccountForm({ ...accountForm, account_end: e.target.value })} data-testid="edit-account-end" />
                <p className="text-xs text-muted-foreground mt-1">Leave blank for ongoing</p>
              </div>
              <div className="col-span-2 border-t pt-3 mt-1">
                <Label className="flex items-center gap-1">Missing Bills — Expected Start Date</Label>
                <Input type="date" className="rounded-none mt-1" value={accountForm.missing_bills_expected_start_date} onChange={(e) => setAccountForm({ ...accountForm, missing_bills_expected_start_date: e.target.value })} data-testid="edit-account-missing-start" />
                <p className="text-xs text-muted-foreground mt-1">When Missing-Bills tracking should begin. Defaults to the oldest bill on platform.</p>
              </div>
              <div className="col-span-2">
                <Label>Service Address</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_address} onChange={(e) => setAccountForm({ ...accountForm, service_address: e.target.value })} />
              </div>
              <div>
                <Label>City</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_city} onChange={(e) => setAccountForm({ ...accountForm, service_city: e.target.value })} />
              </div>
              <div>
                <Label>State</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_state} onChange={(e) => setAccountForm({ ...accountForm, service_state: e.target.value.toUpperCase() })} maxLength={2} />
              </div>
              <div>
                <Label>ZIP Code</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_zip} onChange={(e) => setAccountForm({ ...accountForm, service_zip: e.target.value })} />
              </div>
              <div>
                <Label>Meter Number (from bill)</Label>
                <Input className="rounded-none mt-1 font-mono" value={accountForm.meter_number} onChange={(e) => setAccountForm({ ...accountForm, meter_number: e.target.value })} />
              </div>

              {/* Linking Section */}
              <div className="col-span-2 border-t pt-4 mt-2">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Link className="h-4 w-4" />
                  Link to Property
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label>Property</Label>
                    <Select value={accountForm.linked_location_id || 'none'} onValueChange={(v) => setAccountForm({ ...accountForm, linked_location_id: v === 'none' ? '' : v, linked_space_id: '', linked_meter_id: '' })}>
                      <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select property" /></SelectTrigger>
                      <SelectContent className="rounded-none max-h-60">
                        <SelectItem value="none">-- Not Linked --</SelectItem>
                        {locations.map(loc => (<SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div>
                <Label>BP Entity ID</Label>
                <Input className="rounded-none mt-1 font-mono" value={accountForm.bp_entity_id} onChange={(e) => setAccountForm({ ...accountForm, bp_entity_id: e.target.value })} />
              </div>
              <div>
                <Label>Frequency</Label>
                <Input className="rounded-none mt-1" value={accountForm.frequency} onChange={(e) => setAccountForm({ ...accountForm, frequency: e.target.value })} placeholder="e.g., Monthly" data-testid="edit-account-frequency" />
              </div>
              <div>
                <Label>Bill Source</Label>
                <Input className="rounded-none mt-1" value={accountForm.bill_source} onChange={(e) => setAccountForm({ ...accountForm, bill_source: e.target.value })} placeholder="e.g., Portal, Email" data-testid="edit-account-bill-source" />
              </div>
              <div className="col-span-2 flex items-center gap-2 border-t pt-3 mt-1">
                <Switch id="edit-has-parent" checked={accountForm.has_parent_account} onCheckedChange={(checked) => setAccountForm({ ...accountForm, has_parent_account: !!checked, parent_account_number: checked ? accountForm.parent_account_number : '' })} data-testid="edit-account-has-parent" />
                <Label htmlFor="edit-has-parent" className="cursor-pointer">Has parent account number</Label>
              </div>
              {accountForm.has_parent_account && (
                <div className="col-span-2">
                  <Label>Parent Account No</Label>
                  <Input className="rounded-none mt-1 font-mono" value={accountForm.parent_account_number} onChange={(e) => setAccountForm({ ...accountForm, parent_account_number: e.target.value })} data-testid="edit-account-parent-number" />
                </div>
              )}
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-background">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => { setShowEditAccountModal(false); setEditingAccount(null); resetAccountForm(); }}>Cancel</Button>
              <Button type="submit" className="rounded-none" disabled={saving} data-testid="save-edit-account-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <CollectorOnboardDialog
        open={showOnboardDialog}
        onOpenChange={setShowOnboardDialog}
        accountId={currentAccount?.id}
        suppliers={collectorSuppliers}
        onApplied={fetchData}
      />

      <AccountLinkDialog
        open={showLinkDialog}
        onOpenChange={(o) => { setShowLinkDialog(o); if (!o) setLinkDialogAcct(null); }}
        orgAccountId={currentAccount?.id}
        account={linkDialogAcct}
        meters={meters}
        identifiers={identifiers}
        onSaved={fetchData}
      />

      {/* Add Credential Modal */}
      <Dialog open={showAddCredentialModal} onOpenChange={setShowAddCredentialModal}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Portal Credential</DialogTitle>
            <DialogDescription>
              Add login credentials for a vendor portal. Credentials are stored securely for this organization only.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCredential}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Supplier (Collector) *</Label>
                <SupplierCombobox
                  suppliers={collectorSuppliers}
                  value={credentialForm.collector_supplier}
                  onChange={(shortName, name) => setCredentialForm({ ...credentialForm, collector_supplier: shortName, collector_supplier_name: name })}
                  testId="add-collector-supplier"
                />
                <p className="text-xs text-muted-foreground mt-1">The utility supplier the collector bot logs into to fetch bills. This is also the vendor these bills are recorded under.</p>
              </div>
              <div className="col-span-2">
                <Label>Account Name</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.account_name}
                  onChange={(e) => setCredentialForm({ ...credentialForm, account_name: e.target.value })}
                  placeholder="e.g., Main Office or Company ABC"
                />
              </div>
              <div className="col-span-2">
                <Label>Portal URL</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.portal_url}
                  onChange={(e) => setCredentialForm({ ...credentialForm, portal_url: e.target.value })}
                  placeholder="https://vendor-portal.com/login"
                />
              </div>
              <div>
                <Label>Username</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.username}
                  onChange={(e) => setCredentialForm({ ...credentialForm, username: e.target.value })}
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  className="rounded-none mt-1"
                  type="password"
                  value={credentialForm.password}
                  onChange={(e) => setCredentialForm({ ...credentialForm, password: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Account Number</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.account_number}
                  onChange={(e) => setCredentialForm({ ...credentialForm, account_number: e.target.value })}
                  placeholder="Account number used to login"
                />
              </div>
              <div>
                <Label>BP Credential ID</Label>
                <Input
                  className="rounded-none mt-1 font-mono"
                  value={credentialForm.bp_credential_id}
                  onChange={(e) => setCredentialForm({ ...credentialForm, bp_credential_id: e.target.value })}
                  placeholder="BillPortal reference"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => { setShowAddCredentialModal(false); resetCredentialForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="rounded-none" disabled={saving}>
                {saving ? 'Adding...' : 'Add Credential'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Credential Modal */}
      <Dialog open={showEditCredentialModal} onOpenChange={(open) => { setShowEditCredentialModal(open); if (!open) { setSelectedCredential(null); resetCredentialForm(); } }}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Portal Credential</DialogTitle>
            <DialogDescription>
              Update login credentials. Leave password blank to keep the existing password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateCredential}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Supplier (Collector) *</Label>
                <SupplierCombobox
                  suppliers={collectorSuppliers}
                  value={credentialForm.collector_supplier}
                  onChange={(shortName, name) => setCredentialForm({ ...credentialForm, collector_supplier: shortName, collector_supplier_name: name })}
                  testId="edit-collector-supplier"
                />
                <p className="text-xs text-muted-foreground mt-1">The utility supplier the collector bot logs into to fetch bills. This is also the vendor these bills are recorded under.</p>
              </div>
              <div className="col-span-2">
                <Label>Account Name</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.account_name}
                  onChange={(e) => setCredentialForm({ ...credentialForm, account_name: e.target.value })}
                  placeholder="e.g., Main Office or Company ABC"
                />
              </div>
              <div className="col-span-2">
                <Label>Portal URL</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.portal_url}
                  onChange={(e) => setCredentialForm({ ...credentialForm, portal_url: e.target.value })}
                />
              </div>
              <div>
                <Label>Username</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.username}
                  onChange={(e) => setCredentialForm({ ...credentialForm, username: e.target.value })}
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  className="rounded-none mt-1"
                  type="password"
                  value={credentialForm.password}
                  onChange={(e) => setCredentialForm({ ...credentialForm, password: e.target.value })}
                  placeholder="Leave blank to keep existing"
                />
              </div>
              <div className="col-span-2">
                <Label>Account Number</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.account_number}
                  onChange={(e) => setCredentialForm({ ...credentialForm, account_number: e.target.value })}
                />
              </div>
              <div>
                <Label>BP Credential ID</Label>
                <Input
                  className="rounded-none mt-1 font-mono"
                  value={credentialForm.bp_credential_id}
                  onChange={(e) => setCredentialForm({ ...credentialForm, bp_credential_id: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => { setShowEditCredentialModal(false); setSelectedCredential(null); resetCredentialForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="rounded-none" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Credential Confirmation */}
      <Dialog open={showDeleteCredentialConfirm} onOpenChange={setShowDeleteCredentialConfirm}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Delete Credential</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this portal credential? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowDeleteCredentialConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" className="rounded-none" onClick={handleDeleteCredential} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default VendorAccounts;
